import { useState } from "react";
import { Loader2, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useFindDuplicates } from "../hooks/useLeads";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { MergeLeadsModal } from "./MergeLeadsModal";
import type { LeadDetail } from "../types";

export function FindDuplicatesModal({
  lead,
  open,
  onOpenChange,
}: {
  lead: LeadDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const candidates = useFindDuplicates(lead.id, open);
  const [mergeSource, setMergeSource] = useState<LeadDetail | null>(null);

  return (
    <>
      <Dialog
        open={open && !mergeSource}
        onOpenChange={(o) => onOpenChange(o)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Find duplicate Leads</DialogTitle>
            <DialogDescription>
              Other Leads that share an email, phone, name, or company with{" "}
              <span className="font-mono text-xs">{lead.code}</span>. Pick one
              to compare and merge.
            </DialogDescription>
          </DialogHeader>

          <div
            className="max-h-[420px] overflow-y-auto rounded-md border"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {candidates.isLoading ? (
              <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Scanning…
              </div>
            ) : (candidates.data ?? []).length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
                <Users className="h-5 w-5" strokeWidth={1.5} />
                <div>No likely duplicates found.</div>
                <div className="text-xs">
                  Matching is strict — exact email, exact phone, or same
                  name/company.
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {(candidates.data ?? []).map((c) => (
                  <li
                    key={c.lead.id}
                    className="flex items-start justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {c.lead.code}
                        </span>
                        <span className="truncate text-sm font-medium text-foreground">
                          {c.lead.name ?? "(no name)"}
                        </span>
                        <LeadStatusBadge status={c.lead.status} />
                        <span
                          className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background:
                              c.confidence >= 60
                                ? "hsl(142,55%,92%)"
                                : c.confidence >= 30
                                  ? "hsl(38,92%,92%)"
                                  : "hsl(210,16%,93%)",
                            color:
                              c.confidence >= 60
                                ? "hsl(142,72%,28%)"
                                : c.confidence >= 30
                                  ? "hsl(32,80%,34%)"
                                  : "hsl(215,16%,40%)",
                          }}
                        >
                          {c.confidence}% match
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {c.lead.email ?? "—"}
                        {c.lead.phone ? ` · ${c.lead.phone}` : ""}
                        {c.lead.companyName ? ` · ${c.lead.companyName}` : ""}
                        {" · "}
                        {c.lead.enquiryCount} enquir
                        {c.lead.enquiryCount === 1 ? "y" : "ies"}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Reasons: {c.reasons.join(" · ")}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMergeSource(c.lead)}
                    >
                      Compare & Merge
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mergeSource && (
        <MergeLeadsModal
          target={lead}
          source={mergeSource}
          open={!!mergeSource}
          onOpenChange={(o) => {
            if (!o) {
              setMergeSource(null);
              onOpenChange(false);
            }
          }}
        />
      )}
    </>
  );
}
